import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar, Mail, Phone, Building2, User, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';
import { mockDemoSubmit } from '../../data/mock';
import { toast } from 'sonner';

const DemoFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    busCount: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
        const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}`,
      {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
      }
    );
    const data = await response.json();
    if (data.success) {
        setIsSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            busCount: '',
            message: ''
          });
          setIsSuccess(false);
        }, 3000);
      }
    } catch (error) {
       console.error("Erro completo:", error);

        toast.error(
        typeof error === "string"
        ? error
          : error?.message || "Erro desconhecido"
        );

    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="agendar-demo" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#007bff]/10 px-4 py-2 rounded-full border border-[#007bff]/20 mb-6">
                <Calendar className="w-4 h-4 text-[#007bff]" />
                <span className="text-sm font-medium text-[#007bff]">Agende uma Demo</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Quer o valor exato para sua frota e operação?
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Agende uma demonstração e receba uma proposta alinhada ao seu volume de veículos e tipo de contrato.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#007bff]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-[#007bff]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Demonstração Personalizada
                    </h3>
                    <p className="text-gray-600">
                      Apresentação adaptada às necessidades específicas da sua operação
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#008080]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-[#008080]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Consultoria Gratuita
                    </h3>
                    <p className="text-gray-600">
                      Análise inicial das suas necessidades e recomendações de implementação
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#007bff]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-[#007bff]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Resposta em 24h
                    </h3>
                    <p className="text-gray-600">
                      Nossa equipe entrará em contato rapidamente para agendar a demo
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-gradient-to-r from-[#007bff]/5 to-[#008080]/5 rounded-2xl border border-[#007bff]/20">
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-bold text-gray-900">Mais de 1.000 passageiros</span> já utilizam 
                  o WCOMPass diariamente. Junte-se às empresas que escolheram governança e segurança.
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-100">
                {isSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Demo Agendada!
                    </h3>
                    <p className="text-gray-600 text-lg">
                      Entraremos em contato em breve para confirmar os detalhes.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="flex items-center gap-2 text-gray-700 mb-2">
                        <User className="w-4 h-4" />
                        Nome Completo *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Seu nome"
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="flex items-center gap-2 text-gray-700 mb-2">
                        <Mail className="w-4 h-4" />
                        Email Corporativo *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu.email@empresa.com"
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="flex items-center gap-2 text-gray-700 mb-2">
                        <Phone className="w-4 h-4" />
                        Telefone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" className="flex items-center gap-2 text-gray-700 mb-2">
                        <Building2 className="w-4 h-4" />
                        Empresa *
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Nome da empresa"
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="busCount" className="flex items-center gap-2 text-gray-700 mb-2">
                        <Calendar className="w-4 h-4" />
                        Quantidade de Ônibus
                      </Label>
                      <Input
                        id="busCount"
                        name="busCount"
                        type="number"
                        value={formData.busCount}
                        onChange={handleChange}
                        placeholder="Ex: 10"
                        className="h-12 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="flex items-center gap-2 text-gray-700 mb-2">
                        <MessageSquare className="w-4 h-4" />
                        Mensagem (opcional)
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Conte-nos mais sobre suas necessidades..."
                        className="min-h-[100px] text-base resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 bg-gradient-to-r from-[#007bff] to-[#0056b3] hover:from-[#0056b3] hover:to-[#004085] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Agendando...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-5 h-5 mr-2" />
                          Agendar Demo Gratuita
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Ao enviar, você concorda com nossa política de privacidade
                    </p>
                  </form>
                )}
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#007bff] rounded-full opacity-10 blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#008080] rounded-full opacity-10 blur-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoFormSection;
